import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { FormControl } from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { User } from "@/interfaces";
import { cn, errorHandler } from "@/lib/utils";
import { userService } from "@/services/api/user";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Skeleton } from "./skeleton";

interface DriverSelectProps {
  value?: number | number[];
  onChange: (value: number | number[] | undefined) => void;
  userId?: number;
  type?: 0 | 1 | 2 | 3;
  onDriversLoaded?: () => void;
  multiple?: boolean;
  /** Singular lowercase term used in the texts; defaults to "motorista". */
  noun?: string;
}

const pluralize = (noun: string) =>
  noun.endsWith("r") ? `${noun}es` : `${noun}s`;

const DriverSelect = ({
  value,
  onChange,
  userId,
  type,
  onDriversLoaded,
  multiple = false,
  noun = "motorista",
}: DriverSelectProps) => {
  const [drivers, setDrivers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const nounPlural = pluralize(noun);

  useEffect(() => {
    const loadDrivers = async () => {
      try {
        setLoading(true);
        const response = await userService.getDrivers(userId ?? 0);

        const filteredDrivers =
          type !== undefined
            ? response.data.filter((driver) => driver.type === type)
            : response.data;

        const driversWithLabels = filteredDrivers.map((driver) => ({
          ...driver,
          label: driver.name,
        }));

        setDrivers(driversWithLabels);
        if (onDriversLoaded) {
          onDriversLoaded();
        }
      } catch (err) {
        errorHandler(err, `Erro ao carregar ${pluralize(noun)}.`);
      } finally {
        setLoading(false);
      }
    };

    loadDrivers();
  }, [userId, type, onDriversLoaded, noun]);

  const handleSelect = (driverId: number) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(driverId)
        ? currentValues.filter((id) => id !== driverId)
        : [...currentValues, driverId];
      onChange(newValues);
    } else {
      if (value === driverId) {
        onChange(undefined);
      } else {
        onChange(driverId);
      }
      setOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (multiple) {
      const allDriverIds = drivers.map((driver) => driver.id);
      onChange(allDriverIds);
    }
  };

  const handleDeselectAll = () => {
    if (multiple) {
      onChange([]);
    }
  };

  const getSelectedLabel = () => {
    if (multiple) {
      const selectedDrivers = drivers.filter((driver) =>
        Array.isArray(value) ? value.includes(driver.id) : false,
      );
      return selectedDrivers.length > 0
        ? selectedDrivers.length === 1
          ? selectedDrivers[0].name
          : `${selectedDrivers.length} ${nounPlural} selecionados`
        : `Selecione os ${nounPlural}`;
    } else {
      return value
        ? drivers.find((driver) => driver.id === value)?.name
        : `Selecione um ${noun}`;
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full bg-muted" />{" "}
        <Popover>
          <PopoverContent className="w-full p-2">
            <div className="space-y-2">
              <Skeleton className="h-8 w-full bg-muted" />
              <Skeleton className="h-8 w-full bg-muted" />
              <Skeleton className="h-8 w-full bg-muted" />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FormControl>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={loading}
            className={cn(
              "w-full justify-between dark:border-white overflow-hidden",
              (!value ||
                (multiple && Array.isArray(value) && value.length === 0)) &&
                "text-muted-foreground",
            )}
          >
            <div className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap">
              {getSelectedLabel()}
            </div>

            {value && !multiple ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(undefined);
                }}
                className="mr-1 p-1 hover:bg-accent rounded-full"
              >
                <X className="h-3 w-3" />
              </div>
            ) : null}
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command
          filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}
        >
          <CommandInput placeholder={`Procure um ${noun}...`} className="h-9" />
          <CommandList>
            <CommandEmpty>Nenhum {noun} encontrado.</CommandEmpty>
            {multiple && (
              <CommandGroup>
                <div className="flex gap-2 p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-start"
                    onClick={handleSelectAll}
                  >
                    Marcar todos
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-start"
                    onClick={handleDeselectAll}
                  >
                    Desmarcar todos
                  </Button>
                </div>
              </CommandGroup>
            )}
            <CommandGroup>
              {drivers.map((driver) => (
                <CommandItem
                  value={`${driver.name}-${driver.id}`}
                  key={driver.id}
                  onSelect={() => handleSelect(driver.id)}
                >
                  {driver.name}
                  <Check
                    className={cn(
                      "ml-auto",
                      multiple
                        ? Array.isArray(value) && value.includes(driver.id)
                          ? "opacity-100"
                          : "opacity-0"
                        : driver.id === value
                          ? "opacity-100"
                          : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default DriverSelect;
